<template>
  <div class="pa-4 pa-md-8">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="rgba(255, 255, 255, 0.9)"
      color="#1A237E"
      spinner="mini-spinner"
    />

    <v-snackbar v-model="snackbar" :color="statusColor" rounded="pill" elevation="10">
      <div class="d-flex align-center">
        <v-icon left dark class="mr-2">{{ statusColor === 'success' ? 'mdi-check-circle' : 'mdi-alert' }}</v-icon>
        <span class="font-weight-bold">{{ msg }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <v-row justify="center">
      <v-col cols="12" class="mb-6">
        <div class="d-flex align-center">
          <v-avatar color="blue lighten-5" size="48" class="mr-4">
            <v-icon color="primary">mdi-shield-check-outline</v-icon>
          </v-avatar>
          <div>
            <h2 class="page-title mb-0">Platform Governance</h2>
            <p class="caption grey--text font-weight-bold text-uppercase letter-spaced mb-0">Legal & Privacy Policy Management</p>
          </div>
        </div>
      </v-col>

      <v-col cols="12">
        <v-card flat class="rounded-xl border-light pa-6 elevation-1">
          <v-row>
            <v-col cols="12" md="7">
              <div class="input-label mb-2">Policy Header</div>
              <v-text-field 
                outlined 
                v-model="heading" 
                placeholder="e.g., Global Privacy & Data Protection Policy"
                color="primary"
                class="rounded-lg mb-4"
                background-color="grey lighten-5"
              ></v-text-field>

              <div class="input-label mb-2">Policy Content (Rich Text)</div>
              <div class="editor-wrapper rounded-lg overflow-hidden">
                <vue-editor
                  class="modern-editor"
                  v-model="description"
                  placeholder="Draft your detailed privacy policy here..."
                ></vue-editor>
              </div>

              <div class="mt-8">
                <v-btn 
                  @click="addData" 
                  x-large
                  block
                  depressed
                  dark 
                  color="#1A237E" 
                  class="rounded-pill font-weight-bold letter-spaced shadow-btn"
                >
                  <v-icon left>{{ isUpdate ? 'mdi-refresh' : 'mdi-plus' }}</v-icon>
                  {{ isUpdate ? 'Update Platform Policy' : 'Publish Policy' }}
                </v-btn>
              </div>
            </v-col>

            <v-col cols="12" md="5" class="d-none d-md-block">
              <div class="preview-panel pa-6 rounded-xl blue-grey lighten-5 fill-height border-dashed">
                <div class="text-overline grey--text mb-4">Live Preview</div>
                <div v-if="heading || description" class="preview-content">
                  <h3 class="preview-heading mb-4 primary--text">{{ heading }}</h3>
                  <div class="preview-body ql-editor" v-html="description"></div>
                </div>
                <div v-else class="fill-height d-flex flex-column align-center justify-center grey--text lighten-2">
                  <v-icon size="64" color="grey lighten-2">mdi-text-box-search-outline</v-icon>
                  <p class="mt-4 font-italic">Start typing to see the preview</p>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios"

export default {
    data(){
        return{
            heading: null,
            description: null,
            appLoading: false,
            snackbar: false,
            msg: null,
            statusColor: 'success',
            isUpdate: false // Track if we are editing existing data
        }
    },
    mounted() {
       this.getData()
    },
    methods: {
        addData() {
            if(!this.heading || !this.description) {
                this.msg = "Please complete both heading and description.";
                this.statusColor = "error";
                this.snackbar = true;
                return;
            }

            this.appLoading = true;
            axios({
                method: "post",
                url: "/privacy/policy/addedit",
                headers: { token: localStorage.getItem("token") },
                data: {
                    heading: this.heading,
                    privacypolicy: this.description
                }
            })
            .then((response) => {
               this.appLoading = false;
               this.snackbar = true;
               this.msg = response.data.msg;
               this.statusColor = response.data.status ? 'success' : 'error';
               if(response.data.status) this.isUpdate = true;
            })
            .catch(() => {
                this.appLoading = false;
                this.statusColor = 'error';
                this.msg = "Connection error. Please try again.";
                this.snackbar = true;
            })
        },
        getData() {
            this.appLoading = true;
            axios({
                method: "get",
                url: "/vew/policy",
                headers: { token: localStorage.getItem("token") },
            })
            .then((response) => {
               this.appLoading = false;
               if(response.data.status && response.data.data) {
                  if(response.data.data.policy) {
                      this.description = response.data.data.policy;
                      this.isUpdate = true;
                  }
                  if(response.data.data.heading) {
                      this.heading = response.data.data.heading;
                      this.isUpdate = true;
                  }
               }
            })
            .catch(() => { this.appLoading = false; });
        }
    }
}
</script>

<style scoped>
.page-title {
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  text-transform: uppercase;
  color: #1A237E;
  letter-spacing: 1.5px;
}

.input-label {
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #546E7A;
  letter-spacing: 1px;
}

.border-light { border: 1px solid rgba(0,0,0,0.06) !important; }
.letter-spaced { letter-spacing: 2px !important; }

.editor-wrapper {
  border: 1px solid #E0E0E0;
  background: white;
}

.modern-editor >>> .ql-toolbar {
  border: none !important;
  background-color: #F8FAFC;
  border-bottom: 1px solid #E0E0E0 !important;
}

.modern-editor >>> .ql-container {
  border: none !important;
  min-height: 350px;
}

.preview-panel {
  border: 2px dashed #CFD8DC;
  overflow-y: auto;
  max-height: 600px;
}

.preview-heading {
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  border-left: 4px solid #1A237E;
  padding-left: 15px;
}

.preview-body {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #455A64;
}

.shadow-btn {
  box-shadow: 0 8px 20px rgba(26, 35, 126, 0.3) !important;
}

.border-dashed { border-style: dashed !important; }
</style>